import {
  autoPlaceInto,
  distribute,
  optimize,
  type TrayCard,
} from '../_lib/auto-place'
import { formatHoursDuration } from '../_lib/duration'
import type { PlannerAction } from './actions'
import type { CurriculumState } from './contracts'
import {
  addOneWeek,
  appendCard,
  extractScheduledCard,
  interviewCard,
  isPastWeek,
  libraryCard,
  updateCard,
  weekName,
  weekPublication,
} from './helpers'

export const plannerReducer = (
  state: CurriculumState,
  action: PlannerAction,
): CurriculumState => {
  if (action.type === 'dismiss-flash') return { ...state, flash: null }

  if (action.type === 'add-auto') {
    // The staged card wears the coach's session pick, same as the persisted
    // slice (LG-362 §3): null = the owning source at display time.
    const card = libraryCard(action.item, `assignment-${state.nextId}`, {
      sourceId: action.sourceId,
    })
    const result = autoPlaceInto(state.weeks, card)
    if (!result.placed) {
      return {
        ...state,
        tray: [...state.tray, card],
        nextId: state.nextId + 1,
        flash: `No day has ${formatHoursDuration(card.est)} free — "${card.title}" sent to the tray instead.`,
      }
    }
    return {
      ...state,
      weeks: appendCard(state.weeks, result.weekIdx, result.dayIdx, card),
      nextId: state.nextId + 1,
      flash: result.where,
    }
  }

  if (action.type === 'stage-to-tray') {
    const card = libraryCard(action.item, `assignment-${state.nextId}`, {
      sourceId: action.sourceId,
    })
    return {
      ...state,
      tray: [...state.tray, card],
      nextId: state.nextId + 1,
      flash: `"${card.title}" added to the staging tray.`,
    }
  }

  if (action.type === 'remove-from-tray') {
    const card = state.tray.find((candidate) => candidate.id === action.cardId)
    if (!card) return state
    return {
      ...state,
      tray: state.tray.filter((candidate) => candidate.id !== action.cardId),
      placingId: state.placingId === action.cardId ? null : state.placingId,
      flash: `"${card.title}" removed from the staging tray.`,
    }
  }

  if (action.type === 'toggle-placing') {
    if (!state.tray.some((card) => card.id === action.cardId)) return state
    return {
      ...state,
      placingId: state.placingId === action.cardId ? null : action.cardId,
    }
  }

  if (action.type === 'cancel-placing') return { ...state, placingId: null }

  if (action.type === 'place-at') {
    const card = state.tray.find(
      (candidate) => candidate.id === state.placingId,
    )
    const week = state.weeks[action.weekIdx]
    const day = week?.days[action.dayIdx]
    if (!card || !week || !day || day.kind !== 'normal' || isPastWeek(week)) {
      return state
    }
    return {
      ...state,
      weeks: appendCard(state.weeks, action.weekIdx, action.dayIdx, card),
      tray: state.tray.filter((candidate) => candidate.id !== card.id),
      placingId: null,
      flash: `"${card.title}" placed on ${day.label} (${weekName(week)}) — manual placement.`,
    }
  }

  if (action.type === 'place-card-at') {
    const card = state.tray.find((candidate) => candidate.id === action.cardId)
    const week = state.weeks[action.weekIdx]
    const day = week?.days[action.dayIdx]
    if (!card || !week || !day || day.kind !== 'normal' || isPastWeek(week)) {
      return state
    }
    return {
      ...state,
      weeks: appendCard(state.weeks, action.weekIdx, action.dayIdx, card),
      tray: state.tray.filter((candidate) => candidate.id !== card.id),
      placingId: state.placingId === card.id ? null : state.placingId,
      flash: `"${card.title}" placed on ${day.label} (${weekName(week)}) — dragged.`,
    }
  }

  if (action.type === 'remove-from-day') {
    const { weeks, card } = extractScheduledCard(state.weeks, action.cardId)
    if (!card) return state
    return {
      ...state,
      weeks,
      tray: [...state.tray, card],
      flash: `"${card.title}" moved to the staging tray.`,
    }
  }

  if (action.type === 'move-card-to') {
    // Cross-week reschedule used to be refused here to match the server, which
    // was same-week only. The service now moves a card between weeks (LG-360
    // §4), so refusing would be the divergence rather than the guard against
    // one — a coach would find the same drag working on the persisted board and
    // silently swallowed on this one. The card's own eligibility is still
    // checked by `extractScheduledCard` (helpers.ts), which refuses a done,
    // fixed, or past-week card wherever it is dragged from.
    // Dropping a card back on the day it already occupies is a no-op, but
    // extract-then-append would land it at the bottom of its own column under
    // a "moved" flash. Refuse it before extracting, matching the persisted
    // path's guard in `moveCardTo` so the two models stay in step.
    if (
      state.weeks[action.weekIdx]?.days[action.dayIdx]?.cards.some(
        (card) => card.id === action.cardId,
      )
    ) {
      return state
    }
    const { weeks, card } = extractScheduledCard(state.weeks, action.cardId)
    if (!card) return state
    const week = weeks[action.weekIdx]
    const day = week?.days[action.dayIdx]
    if (!week || !day || day.kind !== 'normal' || isPastWeek(week)) {
      return state
    }
    return {
      ...state,
      weeks: appendCard(weeks, action.weekIdx, action.dayIdx, card),
      flash: `"${card.title}" moved to ${day.label} (${weekName(week)}).`,
    }
  }

  if (action.type === 'distribute-tray') {
    const result = distribute(state.weeks, state.tray)
    return {
      ...state,
      weeks: result.weeks,
      tray: result.leftovers,
      placingId: null,
      flash: result.summary,
    }
  }

  if (action.type === 'optimize-schedule') {
    const result = optimize(state.weeks)
    return { ...state, weeks: result.weeks, flash: result.summary }
  }

  if (action.type === 'publish-week' || action.type === 'unpublish-week') {
    return weekPublication(state, action.weekId, action.type === 'publish-week')
  }

  if (action.type === 'delete-week') {
    const week = state.weeks.find(
      (candidate) =>
        candidate.id === action.weekId && candidate.status === 'draft',
    )
    if (!week) return state
    const movable = week.days.flatMap((day) =>
      day.cards.filter((card) => !card.done && !card.fixed),
    )
    return {
      ...state,
      weeks: state.weeks.filter((candidate) => candidate.id !== action.weekId),
      tray: [...state.tray, ...movable],
      placingId: null,
      flash: `${week.label} deleted — ${movable.length} card(s) moved to the tray.`,
    }
  }

  if (action.type === 'add-week') {
    const week = addOneWeek(state.weeks.at(-1)!)
    return {
      ...state,
      weeks: [...state.weeks, week],
      flash: `${weekName(week)} added — hour budgets carried over from the previous week.`,
    }
  }

  if (action.type === 'set-budget') {
    const week = state.weeks[action.weekIdx]
    const day = week?.days[action.dayIdx]
    if (!week || !day || day.kind !== 'normal' || isPastWeek(week)) return state
    const budget = Math.max(0, Math.min(8, Math.round(action.budget)))
    if (day.budget === budget) return state
    return {
      ...state,
      weeks: state.weeks.map((candidate, weekIdx) =>
        weekIdx !== action.weekIdx
          ? candidate
          : {
              ...candidate,
              days: candidate.days.map((candidateDay, dayIdx) =>
                dayIdx === action.dayIdx
                  ? { ...candidateDay, budget }
                  : candidateDay,
              ),
            },
      ),
    }
  }

  if (action.type === 'toggle-week') {
    if (!state.weeks.some((week) => week.id === action.weekId)) return state
    return {
      ...state,
      weeks: state.weeks.map((week) =>
        week.id === action.weekId ? { ...week, open: !week.open } : week,
      ),
    }
  }

  if (action.type === 'add-custom-card') {
    const week = state.weeks[action.weekIdx]
    const day = week?.days[action.dayIdx]
    if (!week || !day || day.kind !== 'normal' || isPastWeek(week)) return state
    const card: TrayCard = {
      id: `custom-${state.nextId}`,
      title: action.card.title,
      type: action.card.type,
      meta: action.card.meta,
      // Minutes are authoritative; `est` is the derived hours view.
      estimatedMinutes: action.card.estimatedMinutes,
      est: action.card.estimatedMinutes / 60,
      ...(action.card.link === undefined ? {} : { link: action.card.link }),
    }
    return {
      ...state,
      weeks: appendCard(state.weeks, action.weekIdx, action.dayIdx, card),
      nextId: state.nextId + 1,
      flash: `✎ Custom card "${card.title}" added to ${day.label} · ${week.label}.`,
    }
  }

  if (action.type === 'convert-to-ai-mock') {
    let convertedTitle: string | undefined
    const result = updateCard(
      state,
      action.cardId,
      (card) => {
        if (
          card.done ||
          card.fixed ||
          card.type === 'other' ||
          card.type === 'interview' ||
          card.aiMock
        ) {
          return card
        }
        convertedTitle = card.title
        return { ...card, aiMock: true, originalTitle: card.title }
      },
      false,
    )
    if (!convertedTitle) return state
    return {
      ...state,
      weeks: result.weeks,
      tray: result.tray,
      flash: `"${convertedTitle}" converted to an AI mock — Jordan will see only "AI mock interview".`,
    }
  }

  if (action.type === 'revert-ai-mock') {
    let revertedTitle: string | undefined
    const result = updateCard(
      state,
      action.cardId,
      (card) => {
        if (!card.aiMock || card.needsLink) return card
        revertedTitle = card.title
        const {
          aiMock: _aiMock,
          linkedItemId: _linkedItemId,
          needsLink: _needsLink,
          originalTitle: _originalTitle,
          ...normalCard
        } = card
        return normalCard
      },
      false,
    )
    if (!revertedTitle) return state
    return {
      ...state,
      weeks: result.weeks,
      tray: result.tray,
      flash: `"${revertedTitle}" reverted to a normal assignment.`,
    }
  }

  if (action.type === 'link-ai-mock') {
    let linked = false
    const result = updateCard(state, action.cardId, (card) => {
      if (!card.aiMock || !card.needsLink) return card
      linked = true
      return {
        ...libraryCard(action.item, card.id),
        aiMock: true,
        needsLink: false,
        linkedItemId: action.item.id,
        originalTitle: action.item.title,
      }
    })
    if (!linked) return state
    return {
      ...state,
      weeks: result.weeks,
      tray: result.tray,
      flash: `AI mock linked to "${action.item.title}" — Jordan will see only "AI mock interview".`,
    }
  }

  if (action.type === 'add-interview-tier') {
    const card = interviewCard(action.tier, `interview-${state.nextId}`)
    if (action.destination === 'tray') {
      return {
        ...state,
        tray: [...state.tray, card],
        nextId: state.nextId + 1,
        flash:
          action.tier === 'ai'
            ? 'Unlinked AI mock staged — place it, then link a question.'
            : `"${card.title}" added to the staging tray.`,
      }
    }
    const result = autoPlaceInto(state.weeks, card)
    if (!result.placed) {
      return {
        ...state,
        tray: [...state.tray, card],
        nextId: state.nextId + 1,
        flash:
          action.tier === 'ai'
            ? 'No day had room — unlinked AI mock sent to the tray.'
            : `No day has ${formatHoursDuration(card.est)} free — "${card.title}" sent to the tray instead.`,
      }
    }
    const where = `${state.weeks[result.weekIdx].days[result.dayIdx].label} (${weekName(state.weeks[result.weekIdx])})`
    return {
      ...state,
      weeks: appendCard(state.weeks, result.weekIdx, result.dayIdx, card),
      nextId: state.nextId + 1,
      flash:
        action.tier === 'ai'
          ? `AI mock placed on ${where} — now link a question to it (⚠ on the card).`
          : result.where,
    }
  }

  return state
}
